import type { ReactNode } from "react";

import { Panel } from "./panel";

/**
 * The panel id `KeepMountedPanel` renders for a given hidden state. Consumers
 * use this to build the matching PanelGroup `panelIds` / `defaultLayout`.
 */
export const keepMountedPanelId = (id: string, hidden: boolean) => (hidden ? `${id}-mount` : id);

export interface KeepMountedPanelProps {
  id: string;
  hidden: boolean;
  children: ReactNode;
  /** Min size when visible; ignored while hidden. */
  minSize?: number | string;
  /** Default size when visible; ignored while hidden. */
  defaultSize?: number | string;
  /** Max size when visible; ignored while hidden. */
  maxSize?: number | string;
  className?: string;
}

/**
 * A panel that stays mounted (its subtree is preserved) but occupies zero width
 * while `hidden`. Swaps its id to `${id}-mount` when hidden so the saved
 * PanelGroup layout for the visible panel set is not clobbered.
 *
 * Unlike `CollapsiblePanel`, the hidden subtree is NOT wrapped in
 * `aria-hidden`/`inert`: consumers keep it mounted specifically to preserve
 * state, and are expected to gate their own focusable content while hidden
 * (e.g. the chat column renders nothing via its `mountOnly` prop). Do not
 * render focusable content here while `hidden`.
 */
export const KeepMountedPanel = ({
  id,
  hidden,
  children,
  minSize,
  defaultSize,
  maxSize,
  className,
}: KeepMountedPanelProps) => (
  <Panel
    id={keepMountedPanelId(id, hidden)}
    minSize={hidden ? "0%" : minSize}
    defaultSize={hidden ? "0%" : defaultSize}
    maxSize={hidden ? "0%" : maxSize}
    collapsedSize="0%"
    collapsible={hidden}
    className={className}
  >
    {children}
  </Panel>
);
